import React, { useEffect } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { CustomCircularProgress } from 'Components/StyledComponents';
import PaymentFlow from './Payment/PaymentFlow';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import PendingDeletionModal from './PendingDeletionModal';
import { hasAuthParams } from 'react-oidc-context';
type ProtectedLayoutProps = { children: React.ReactNode };
const ProtectedLayout: React.FC<ProtectedLayoutProps> = ({ children }) => (
  <>
    <PaymentFlow />
    {children}
    <PendingDeletionModal />
  </>
);

type ProtectedRouteProps = {
  redirectPath: string;
} & React.PropsWithChildren;

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, redirectPath }) => {
  const { isAuthenticated, isLoading, oauthPending, authInitialized } = useAuthWrapperContext();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if we're in the middle of an OAuth LOGIN callback (has auth params in URL)
  const isOAuthLoginCallback = hasAuthParams();
  const isCheckingAuth = !authInitialized || isLoading || oauthPending || isOAuthLoginCallback;

  useEffect(() => {
    console.log('[MUS-61] ProtectedRoute effect', {
      path: location.pathname,
      isAuthenticated,
      isLoading,
      oauthPending,
      authInitialized,
      isOAuthLoginCallback,
    });
    if (location.pathname == '/' && !isCheckingAuth && !isAuthenticated) {
      console.log('[MUS-61] ProtectedRoute: unauthenticated at /, navigating to /signin');
      navigate('/signin');
    } else if (location.pathname == '/' && !isCheckingAuth && isAuthenticated) {
      console.log('[MUS-61] ProtectedRoute: authenticated at /, navigating to /roadmap');
      navigate('/roadmap');
    }
  }, [
    authInitialized,
    isAuthenticated,
    isCheckingAuth,
    isLoading,
    isOAuthLoginCallback,
    location.pathname,
    navigate,
    oauthPending,
  ]);

  // Show loading while persisted/auth-provider state is still being resolved.
  if (isCheckingAuth && !isAuthenticated) {
    return (
      <Box
        sx={{
          height: '100vh',
          width: '100vw',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CustomCircularProgress />
      </Box>
    );
  }

  if (!isAuthenticated && !isCheckingAuth) {
    return <Navigate to={redirectPath} replace />;
  }

  return <ProtectedLayout>{children}</ProtectedLayout>;
};

export default ProtectedRoute;
